import {
  html,
  attr,
  on,
  Attr,
  computed,
  effect,
  KeyedForEach,
  MapSignal,
  WithElement,
  type Prop,
} from '@tempots/dom'
import {
  PAGE_SIZES,
  type BorderSide,
  type HeaderComponent,
  type TextBlock,
  type WatermarkBlock,
  type ImageBlock,
  type ContainerBlock,
  type ResumeBlock,
} from '@rh/shared/schemas/design'
import {
  textStyle,
  containerStyle,
  imageOuterStyle,
  imageInnerStyle,
  resumeSlotStyle,
} from '@rh/shared/templates/design-renderer'
import { AgencyAssetService } from '@/services/agency-asset-service'
import { normalizeHexColor } from '@/utils/color'
import {
  gridCellSize,
  WATERMARK_ID,
  type DesignEditorState,
} from './design-state'
import type { DesignActions } from './design-actions'
import {
  initOverlay,
  keepToolbarOnPage,
  placePanelClearOfComponent,
  renderEditingOverlay,
  renderFormattingToolbar,
} from './text-toolbar'
import { tokensToPreview } from './token-chips'

type CompSignal = { value: HeaderComponent }

const MIN_SIZE = 20

// Alignment guides: while moving, the element's edges/center snap to other
// elements' edges/centers and to the page edges/center when within this
// many pixels, with a 1px guide line showing the match.
const ALIGN_TOLERANCE = 4

interface DragState {
  compId: string
  type: 'move' | 'resize' | 'rotate'
  handle: string
  startX: number
  startY: number
  origX: number
  origY: number
  origW: number
  origH: number
  origRotation: number
  rotateCenter: { cx: number; cy: number; startAngle: number } | null
  /** Alignment anchor coordinates (page + other components), move drags only */
  alignTargets: { xs: AlignTarget[]; ys: AlignTarget[] } | null
}

interface AlignTarget {
  coord: number
  /** The matched element's extent on the perpendicular axis, so the guide
   *  line can span from the dragged element to what it aligned with. */
  start: number
  end: number
}

/**
 * Nearest alignment match for one axis: tries the element's leading edge,
 * center, and trailing edge against every target coordinate. Returns the
 * adjusted position, the matched coordinate (where the guide line draws),
 * and the matched target's perpendicular extent.
 */
function findAlignment(
  pos: number,
  size: number,
  targets: AlignTarget[]
): {
  pos: number
  guide: number
  targetStart: number
  targetEnd: number
} | null {
  let best: {
    pos: number
    guide: number
    targetStart: number
    targetEnd: number
    dist: number
  } | null = null
  for (const anchorOffset of [0, size / 2, size]) {
    const anchor = pos + anchorOffset
    for (const t of targets) {
      const dist = Math.abs(t.coord - anchor)
      if (dist <= ALIGN_TOLERANCE && (!best || dist < best.dist)) {
        best = {
          pos: t.coord - anchorOffset,
          guide: t.coord,
          targetStart: t.start,
          targetEnd: t.end,
          dist,
        }
      }
    }
  }
  return best
    ? {
        pos: best.pos,
        guide: best.guide,
        targetStart: best.targetStart,
        targetEnd: best.targetEnd,
      }
    : null
}

/**
 * Wires up window-level drag/resize/rotate handling for a canvas instance.
 * Mirrors the sandbox's geometry math (clamped to page bounds, aspect-ratio
 * lock for corner-resizing images). Returns handle-attachment functions to
 * use in the rendered markup, and a disposer to remove the listeners.
 */
function createDragController(
  state: DesignEditorState,
  actions: DesignActions,
  pageElementSelector: string
) {
  let drag: DragState | null = null
  let moved = false
  let suppressNextClick = false

  // Live angle readout while rotating, floated above the component so a
  // known rotation can be matched by eye. Managed imperatively alongside
  // the drag (appears on grab, tracks every move, removed on release).
  function showRotationBadge(
    comp: { x: number; y: number; width: number },
    rotation: number
  ) {
    const pageEl = document.querySelector<HTMLElement>(pageElementSelector)
    if (!pageEl) return
    let badge = pageEl.querySelector<HTMLElement>('.rotation-angle-badge')
    if (!badge) {
      badge = document.createElement('div')
      badge.className = 'rotation-angle-badge'
      badge.style.cssText = [
        'position:absolute; transform:translateX(-50%);',
        'background:#1e293b; color:#e2e8f0; font-size:13px;',
        'padding:3px 9px; border-radius:4px; white-space:nowrap;',
        'pointer-events:none; z-index:30;',
        'box-shadow:0 2px 8px rgba(0,0,0,0.35);',
      ].join(' ')
      pageEl.appendChild(badge)
    }
    const norm = Math.round(((rotation % 360) + 360) % 360)
    badge.textContent = `${norm}°`
    badge.style.left = `${comp.x + comp.width / 2}px`
    badge.style.top = `${Math.max(4, comp.y - 78)}px`
  }

  function hideRotationBadge() {
    document.querySelector('.rotation-angle-badge')?.remove()
  }

  // 1px guide lines shown while a move drag is aligned with the page or
  // another element (Illustrator-style). Each line spans from the dragged
  // element to whatever it matched, so it's clear WHAT is aligned. One per
  // axis, upserted imperatively per move tick, removed on drop.
  function upsertGuide(
    dir: 'v' | 'h',
    coord: number | null,
    start = 0,
    end = 0
  ) {
    const pageEl = document.querySelector<HTMLElement>(pageElementSelector)
    if (!pageEl) return
    const cls = dir === 'v' ? 'align-guide-v' : 'align-guide-h'
    let line = pageEl.querySelector<HTMLElement>(`.${cls}`)
    if (coord === null) {
      line?.remove()
      return
    }
    if (!line) {
      line = document.createElement('div')
      line.className = cls
      line.style.cssText = [
        'position:absolute; background:#ff3366; pointer-events:none; z-index:40;',
        dir === 'v' ? 'width:1px;' : 'height:1px;',
      ].join(' ')
      pageEl.appendChild(line)
    }
    if (dir === 'v') {
      line.style.left = `${coord}px`
      line.style.top = `${start}px`
      line.style.height = `${end - start}px`
    } else {
      line.style.top = `${coord}px`
      line.style.left = `${start}px`
      line.style.width = `${end - start}px`
    }
  }

  function hideAlignmentGuides() {
    upsertGuide('v', null)
    upsertGuide('h', null)
  }

  function onMouseMove(e: MouseEvent) {
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (!moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return
    moved = true

    const comp = state.components.value.find(c => c.id === drag!.compId)
    if (!comp) return

    if (drag.type === 'rotate' && drag.rotateCenter) {
      const pageEl = document.querySelector(pageElementSelector)
      if (!pageEl) return
      const rect = pageEl.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top
      const currentAngle = Math.atan2(
        mouseY - drag.rotateCenter.cy,
        mouseX - drag.rotateCenter.cx
      )
      const delta =
        (currentAngle - drag.rotateCenter.startAngle) * (180 / Math.PI)
      let rotation = drag.origRotation + delta
      // 15deg snap only while grid snapping is on, matching the sandbox
      if (state.gridSnap.value) rotation = Math.round(rotation / 15) * 15
      actions.updateComponent({ ...comp, rotation }, false)
      showRotationBadge(comp, rotation)
      return
    }

    const { origX, origY, origW, origH } = drag
    let x = origX
    let y = origY
    let width = origW
    let height = origH
    const isCorner = ['ne', 'nw', 'se', 'sw'].includes(drag.handle)
    const lockRatio =
      drag.type === 'resize' && isCorner && comp.type === 'image'
    const ratio = origW / origH

    if (drag.type === 'move') {
      x = Math.max(0, origX + dx)
      y = Math.max(0, origY + dy)
    } else {
      switch (drag.handle) {
        case 'e':
          width = Math.max(MIN_SIZE, origW + dx)
          break
        case 'w':
          x = origX + dx
          width = Math.max(MIN_SIZE, origW - dx)
          break
        case 's':
          height = Math.max(MIN_SIZE, origH + dy)
          break
        case 'n':
          y = origY + dy
          height = Math.max(MIN_SIZE, origH - dy)
          break
        case 'se':
          width = Math.max(MIN_SIZE, origW + dx)
          height = lockRatio ? width / ratio : Math.max(MIN_SIZE, origH + dy)
          break
        case 'sw':
          width = Math.max(MIN_SIZE, origW - dx)
          x = origX + origW - width
          height = lockRatio ? width / ratio : Math.max(MIN_SIZE, origH + dy)
          break
        case 'ne':
          width = Math.max(MIN_SIZE, origW + dx)
          height = lockRatio ? width / ratio : Math.max(MIN_SIZE, origH - dy)
          y = origY + origH - height
          break
        case 'nw':
          width = Math.max(MIN_SIZE, origW - dx)
          x = origX + origW - width
          height = lockRatio ? width / ratio : Math.max(MIN_SIZE, origH - dy)
          y = origY + origH - height
          break
      }
    }

    // Alignment matches are found against the RAW cursor position, before
    // grid quantization, so intent within tolerance always registers.
    const alignX = drag.alignTargets
      ? findAlignment(x, width, drag.alignTargets.xs)
      : null
    const alignY = drag.alignTargets
      ? findAlignment(y, height, drag.alignTargets.ys)
      : null

    // Snap to grid
    if (state.gridSnap.value) {
      const sz = gridCellSize(state)
      if (drag.type === 'move') {
        x = Math.round(x / sz) * sz
        y = Math.round(y / sz) * sz
      } else {
        width = Math.max(MIN_SIZE, Math.round(width / sz) * sz)
        if (!lockRatio)
          height = Math.max(MIN_SIZE, Math.round(height / sz) * sz)
        else height = width / ratio // preserve ratio after width snap
      }
    }

    // Alignment overrides the grid on its axis — it's the more specific intent
    let guideX: number | null = null
    let guideY: number | null = null
    if (alignX) {
      x = alignX.pos
      guideX = alignX.guide
    }
    if (alignY) {
      y = alignY.pos
      guideY = alignY.guide
    }

    const { w: pgW, h: pgH } = PAGE_SIZES[state.pageSize.value]
    if (drag.type === 'move') {
      x = Math.max(0, Math.min(x, pgW - width))
      y = Math.max(0, Math.min(y, pgH - height))
      // Clamping can break an exact match near the page edge — only show
      // guides that still hold on the final position.
      const holds = (guide: number, pos: number, size: number) =>
        [pos, pos + size / 2, pos + size].some(a => Math.abs(a - guide) < 0.5)
      if (guideX !== null && !holds(guideX, x, width)) guideX = null
      if (guideY !== null && !holds(guideY, y, height)) guideY = null
      // Span each line from the dragged element to what it matched
      if (guideX !== null && alignX) {
        upsertGuide(
          'v',
          guideX,
          Math.min(alignX.targetStart, y),
          Math.max(alignX.targetEnd, y + height)
        )
      } else {
        upsertGuide('v', null)
      }
      if (guideY !== null && alignY) {
        upsertGuide(
          'h',
          guideY,
          Math.min(alignY.targetStart, x),
          Math.max(alignY.targetEnd, x + width)
        )
      } else {
        upsertGuide('h', null)
      }
    } else {
      if (x < 0) {
        width += x
        x = 0
      }
      if (y < 0) {
        height += y
        y = 0
      }
      if (x + width > pgW) width = pgW - x
      if (y + height > pgH) height = pgH - y
    }

    actions.updateComponent({ ...comp, x, y, width, height }, false)
  }

  function onMouseUp() {
    if (!drag) return
    hideRotationBadge()
    hideAlignmentGuides()
    const wasDragged = moved
    drag = null
    moved = false
    document.body.style.userSelect = ''
    if (wasDragged) {
      // The browser still fires a click after mouseup; swallow it so a
      // drag-to-move never also triggers click actions on the element
      // (re-select is harmless, but an empty image would open its file
      // picker).
      suppressNextClick = true
      actions.pushHistory()
    }
  }

  function onClickCapture(e: MouseEvent) {
    if (!suppressNextClick) return
    suppressNextClick = false
    e.stopPropagation()
    e.preventDefault()
  }

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
  document.addEventListener('click', onClickCapture, true)

  function startDrag(
    e: MouseEvent,
    compId: string,
    type: 'move' | 'resize',
    handle = ''
  ) {
    e.preventDefault()
    e.stopPropagation()
    const comp = state.components.value.find(c => c.id === compId)
    if (!comp) return
    // Alignment anchors for move drags: page edges/center plus every other
    // component's edges/center (unrotated bounding boxes).
    let alignTargets: DragState['alignTargets'] = null
    if (type === 'move') {
      const { w: pgW, h: pgH } = PAGE_SIZES[state.pageSize.value]
      // Page alignments span the whole page; element alignments carry the
      // element's extent so the guide can bridge the two aligned elements.
      const pageV = { start: 0, end: pgH }
      const pageH2 = { start: 0, end: pgW }
      const xs: AlignTarget[] = [
        { coord: 0, ...pageV },
        { coord: pgW / 2, ...pageV },
        { coord: pgW, ...pageV },
      ]
      const ys: AlignTarget[] = [
        { coord: 0, ...pageH2 },
        { coord: pgH / 2, ...pageH2 },
        { coord: pgH, ...pageH2 },
      ]
      for (const c of state.components.value) {
        if (c.id === compId) continue
        const vSpan = { start: c.y, end: c.y + c.height }
        const hSpan = { start: c.x, end: c.x + c.width }
        xs.push(
          { coord: c.x, ...vSpan },
          { coord: c.x + c.width / 2, ...vSpan },
          { coord: c.x + c.width, ...vSpan }
        )
        ys.push(
          { coord: c.y, ...hSpan },
          { coord: c.y + c.height / 2, ...hSpan },
          { coord: c.y + c.height, ...hSpan }
        )
      }
      alignTargets = { xs, ys }
    }
    drag = {
      compId,
      type,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      origX: comp.x,
      origY: comp.y,
      origW: comp.width,
      origH: comp.height,
      origRotation: comp.rotation,
      rotateCenter: null,
      alignTargets,
    }
    moved = false
    document.body.style.userSelect = 'none'
  }

  function startRotate(e: MouseEvent, compId: string) {
    e.preventDefault()
    e.stopPropagation()
    const comp = state.components.value.find(c => c.id === compId)
    if (!comp) return
    const cx = comp.x + comp.width / 2
    const cy = comp.y + comp.height / 2
    const pageEl = document.querySelector(pageElementSelector)
    if (!pageEl) return
    const rect = pageEl.getBoundingClientRect()
    const startAngle = Math.atan2(
      e.clientY - rect.top - cy,
      e.clientX - rect.left - cx
    )
    drag = {
      compId,
      type: 'rotate',
      handle: '',
      startX: e.clientX,
      startY: e.clientY,
      origX: comp.x,
      origY: comp.y,
      origW: comp.width,
      origH: comp.height,
      origRotation: comp.rotation,
      rotateCenter: { cx, cy, startAngle },
      alignTargets: null,
    }
    moved = false
    document.body.style.userSelect = 'none'
    // Show the current angle immediately on grab, before any movement
    showRotationBadge(comp, comp.rotation)
  }

  function dispose() {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
    document.removeEventListener('click', onClickCapture, true)
  }

  return { startDrag, startRotate, dispose }
}

type DragController = ReturnType<typeof createDragController>

function selectionOutline(selected: boolean, hovered: boolean): string {
  if (selected) return 'outline: 2px solid #3b82f6; outline-offset: 1px;'
  if (hovered) return 'outline: 2px dashed #93c5fd; outline-offset: 1px;'
  return ''
}

// Fixed base size of the fake-resume mockup; it's scaled to fit the block.
const RESUME_BASE_W = 646
const RESUME_BASE_H = 833

function renderResumeBlock(
  compSignal: CompSignal,
  id: string,
  state: DesignEditorState
) {
  const displayId = `resume-display-${id}`

  const style = computed(() => {
    const base = resumeSlotStyle(compSignal.value as ResumeBlock)
    const outline = selectionOutline(
      state.selectedId.value === id,
      state.hoveredId.value === id && state.selectedId.value !== id
    )
    return `${base} ${outline} box-sizing:border-box; overflow:hidden; border-radius:4px; border:1px dashed #cbd5e1;`
  }, [
    state.selectedId,
    state.hoveredId,
    compSignal as unknown as Prop<unknown>,
  ])

  // Contain the resume mockup inside the resize box, maintaining page ratio, centered
  effect(() => {
    const comp = compSignal.value as ResumeBlock
    requestAnimationFrame(() => {
      const el = document.getElementById(displayId)
      if (!el) return
      const inner = el.querySelector('.resume-inner') as HTMLElement | null
      if (!inner) return

      const { w: pgW, h: pgH } = PAGE_SIZES[state.pageSize.value]
      const pageRatio = pgW / pgH
      const boxRatio = comp.width / comp.height

      let innerW: number, innerH: number
      if (boxRatio > pageRatio) {
        // Box wider than page ratio — fill height, center horizontally
        innerH = comp.height
        innerW = comp.height * pageRatio
      } else {
        // Box taller than page ratio — fill width, center vertically
        innerW = comp.width
        innerH = comp.width / pageRatio
      }

      const offsetX = (comp.width - innerW) / 2
      const offsetY = (comp.height - innerH) / 2
      const sx = innerW / RESUME_BASE_W
      const sy = innerH / RESUME_BASE_H

      inner.style.left = `${offsetX}px`
      inner.style.top = `${offsetY}px`
      inner.style.transform = `scale(${sx},${sy})`
    })
  }, [compSignal as unknown as Prop<unknown>, state.pageSize])

  // Helpers for placeholder content
  const bar = (w: string, h = 8, mb = 6) =>
    html.div(
      attr.style(
        `width:${w};height:${h}px;background:#e2e2e2;border-radius:3px;margin-bottom:${mb}px;`
      )
    )
  const heading = (w: string) =>
    html.div(
      attr.style(
        `width:${w};height:11px;background:#c8c8c8;border-radius:3px;margin-bottom:4px;margin-top:20px;`
      )
    )
  const sep = () =>
    html.div(
      attr.style('width:100%;height:1px;background:#ddd;margin-bottom:10px;')
    )
  const bullet = (w: string) =>
    html.div(
      attr.style('display:flex;align-items:center;gap:6px;margin-bottom:5px;'),
      html.div(
        attr.style(
          'width:4px;height:4px;border-radius:50%;background:#ccc;flex-shrink:0;'
        )
      ),
      html.div(
        attr.style(
          `width:${w};height:7px;background:#e8e8e8;border-radius:3px;`
        )
      )
    )

  return html.div(
    Attr('id', displayId),
    Attr('data-component-id', id),
    attr.style(style),
    on.mouseenter(() => state.hoveredId.set(id)),
    on.mouseleave(() => {
      if (state.hoveredId.value === id) state.hoveredId.set(null)
    }),
    on.click((e: MouseEvent) => {
      e.stopPropagation()
      state.selectedId.set(id)
    }),

    // Resume mockup — fixed base size, absolutely positioned and scaled to
    // contain within the resize box
    html.div(
      attr.class('resume-inner'),
      attr.style(
        `position:absolute; width:${RESUME_BASE_W}px; height:${RESUME_BASE_H}px; padding:24px 28px; user-select:none; pointer-events:none; transform-origin:top left; background:#fafafa; border-radius:3px; box-shadow:0 1px 4px rgba(0,0,0,0.08); box-sizing:border-box;`
      ),

      // Summary
      heading('90px'),
      sep(),
      bar('95%', 7),
      bar('88%', 7),
      bar('72%', 7),

      // Professional Experience
      heading('170px'),
      sep(),
      bar('55%'),
      bullet('85%'),
      bullet('70%'),
      bullet('92%'),
      bullet('68%'),
      html.div(attr.style('height:8px;')),
      bar('50%'),
      bullet('78%'),
      bullet('65%'),
      bullet('88%'),
      bullet('74%'),
      html.div(attr.style('height:8px;')),
      bar('48%'),
      bullet('82%'),
      bullet('60%'),
      bullet('90%'),
      html.div(attr.style('height:8px;')),
      bar('52%'),
      bullet('76%'),
      bullet('83%'),
      bullet('62%'),

      // Education
      heading('95px'),
      sep(),
      bar('65%'),
      bar('45%', 7),
      html.div(attr.style('height:4px;')),
      bar('58%'),
      bar('40%', 7),

      // Skills
      heading('55px'),
      sep(),
      html.div(
        attr.style('display:flex;flex-wrap:wrap;gap:6px;margin-bottom:6px;'),
        ...[
          '58px',
          '76px',
          '52px',
          '68px',
          '48px',
          '62px',
          '72px',
          '44px',
          '66px',
          '54px',
          '70px',
          '56px',
          '64px',
          '50px',
        ].map(w =>
          html.div(
            attr.style(
              `width:${w};height:18px;background:#ececec;border-radius:9px;`
            )
          )
        )
      ),

      // Certifications & Awards
      heading('150px'),
      sep(),
      bar('60%'),
      bar('55%', 7),
      html.div(attr.style('height:4px;')),
      bar('52%'),
      bar('48%', 7),

      // Volunteer & Interests
      heading('155px'),
      sep(),
      bar('58%'),
      bullet('80%'),
      bullet('72%'),
      html.div(attr.style('height:8px;')),
      bar('50%'),
      bullet('75%'),
      bullet('68%')
    )
  )
}

function renderTextBlock(
  compSignal: CompSignal,
  id: string,
  state: DesignEditorState
) {
  const displayId = `text-display-${id}`

  effect(() => {
    const comp = compSignal.value as TextBlock | WatermarkBlock
    // Unselected blocks show tokens RESOLVED to the current user's values
    // (real preview); the editing overlay shown while selected is where
    // chips appear. Empty tokens keep a muted chip so lines hold shape.
    const preview = tokensToPreview(comp.content, state.tokenData.value)
    requestAnimationFrame(() => {
      const el = document.getElementById(displayId)
      if (el && document.activeElement !== el) el.innerHTML = preview
    })
  }, [
    compSignal as unknown as Prop<unknown>,
    state.tokenData as unknown as Prop<unknown>,
  ])

  const style = computed(() => {
    const base = textStyle(compSignal.value as TextBlock | WatermarkBlock)
    const selected = state.selectedId.value === id
    const hovered = state.hoveredId.value === id && !selected
    const border = selected
      ? 'border: 2px solid #3b82f6; background: rgba(239,246,255,0.35);'
      : hovered
        ? 'border: 2px dashed #93c5fd;'
        : 'border: 2px dashed transparent;'
    // While selected, the editing overlay (a contenteditable div positioned
    // identically) renders on top — hide this display div so its content
    // doesn't show through underneath / duplicate.
    const visibility = selected ? 'visibility: hidden;' : ''
    return `${base} ${border} ${visibility} cursor:text; box-sizing:border-box;`
  }, [
    state.selectedId,
    state.hoveredId,
    compSignal as unknown as Prop<unknown>,
  ])

  return html.div(
    Attr('id', displayId),
    Attr('data-component-id', id),
    attr.style(style),
    on.mouseenter(() => state.hoveredId.set(id)),
    on.mouseleave(() => {
      if (state.hoveredId.value === id) state.hoveredId.set(null)
    }),
    on.click((e: MouseEvent) => {
      e.stopPropagation()
      state.selectedId.set(id)
      // Open the editing overlay with the cursor at the end, matching the
      // sandbox's click-to-edit behavior.
      requestAnimationFrame(() =>
        initOverlay((compSignal.value as TextBlock | WatermarkBlock).content)
      )
    })
  )
}

function renderContainerBlock(
  compSignal: CompSignal,
  id: string,
  state: DesignEditorState,
  drag: DragController
) {
  const style = computed(() => {
    const base = containerStyle(compSignal.value as ContainerBlock)
    const outline = selectionOutline(
      state.selectedId.value === id,
      state.hoveredId.value === id && state.selectedId.value !== id
    )
    return `${base} ${outline} cursor:move;`
  }, [
    state.selectedId,
    state.hoveredId,
    compSignal as unknown as Prop<unknown>,
  ])
  return html.div(
    Attr('data-component-id', id),
    attr.style(style),
    on.mouseenter(() => state.hoveredId.set(id)),
    on.mouseleave(() => {
      if (state.hoveredId.value === id) state.hoveredId.set(null)
    }),
    // A solid element moves by dragging anywhere on it.
    on.mousedown((e: MouseEvent) => {
      state.selectedId.set(id)
      drag.startDrag(e, id, 'move')
    }),
    on.click((e: MouseEvent) => {
      e.stopPropagation()
      state.selectedId.set(id)
    })
  )
}

function renderImageBlock(
  compSignal: CompSignal,
  id: string,
  state: DesignEditorState,
  actions: DesignActions,
  agencyId: string,
  drag: DragController
) {
  const displayId = `image-display-${id}`

  async function uploadFile(file: File) {
    if (!file.type.startsWith('image/')) return
    try {
      const result = await AgencyAssetService.uploadDesignImageAsset(
        agencyId,
        file
      )
      const comp = state.components.value.find(c => c.id === id)
      if (!comp || comp.type !== 'image') return
      const img = new Image()
      const objectUrl = URL.createObjectURL(file)
      img.onload = () => {
        actions.updateComponent(
          {
            ...comp,
            assetKey: result.assetKey,
            originalWidth: img.naturalWidth,
            originalHeight: img.naturalHeight,
          },
          true
        )
        URL.revokeObjectURL(objectUrl)
      }
      img.src = objectUrl
    } catch (error) {
      console.error('Failed to upload design image', error)
    }
  }

  const style = computed(() => {
    const comp = compSignal.value as ImageBlock
    const base = imageOuterStyle(comp)
    const selected = state.selectedId.value === id
    const hovered = state.hoveredId.value === id && !selected
    const border = selected
      ? 'border: 2px solid #3b82f6;'
      : hovered
        ? 'border: 2px dashed #93c5fd;'
        : comp.assetKey
          ? 'border: 2px dashed transparent;'
          : 'border: 2px dashed #94a3b8;'
    // Empty placeholders read as "click to upload"; filled images read as
    // "drag to move" (both actually support both).
    const cursor = comp.assetKey ? 'cursor:move;' : 'cursor:pointer;'
    return `${base} ${border} ${cursor} box-sizing:border-box;`
  }, [
    state.selectedId,
    state.hoveredId,
    compSignal as unknown as Prop<unknown>,
  ])

  const backgroundUrl = computed(() => {
    const comp = compSignal.value as ImageBlock
    return comp.assetKey
      ? AgencyAssetService.getDesignImageAssetUrl(agencyId, comp.assetKey)
      : undefined
  }, [compSignal as unknown as Prop<unknown>])

  return html.div(
    Attr('id', displayId),
    Attr('data-component-id', id),
    attr.style(style),
    on.mouseenter(() => state.hoveredId.set(id)),
    on.mouseleave(() => {
      if (state.hoveredId.value === id) state.hoveredId.set(null)
    }),
    // A solid element moves by dragging anywhere on it; a plain click on an
    // empty placeholder still opens the file picker (the drag controller
    // swallows the click only after an actual drag).
    on.mousedown((e: MouseEvent) => {
      state.selectedId.set(id)
      drag.startDrag(e, id, 'move')
    }),
    on.click((e: MouseEvent) => {
      e.stopPropagation()
      state.selectedId.set(id)
      const comp = compSignal.value as ImageBlock
      if (!comp.assetKey) {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = () => {
          if (input.files?.[0]) void uploadFile(input.files[0])
        }
        input.click()
      }
    }),
    on.dragover((e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }),
    on.drop((e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const file = e.dataTransfer?.files[0]
      if (file) void uploadFile(file)
    }),
    html.div(
      attr.style(
        computed(() => {
          const url = backgroundUrl.value
          const fit = (compSignal.value as ImageBlock).fit
          return `width:100%;height:100%;pointer-events:none;${url ? imageInnerStyle(url, fit) : ''}`
        }, [backgroundUrl, compSignal as unknown as Prop<unknown>])
      )
    ),
    MapSignal(backgroundUrl, url =>
      url
        ? html.div()
        : html.div(
            attr.style(
              'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;position:absolute;inset:0;color:#94a3b8;user-select:none;background:#f8fafc;pointer-events:none;'
            ),
            html.span(attr.style('font-size:11px;'), 'Click or drop image')
          )
    )
  )
}

function parseColor(color: string): { hex: string; opacity: number } {
  if (!color || color === 'transparent' || color === 'none') {
    return { hex: '#000000', opacity: 0 }
  }
  const m = color.match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/
  )
  if (m) {
    const toHex = (n: number) => n.toString(16).padStart(2, '0')
    return {
      hex: `#${toHex(+m[1])}${toHex(+m[2])}${toHex(+m[3])}`,
      opacity: m[4] !== undefined ? parseFloat(m[4]) : 1,
    }
  }
  return { hex: color.startsWith('#') ? color : '#000000', opacity: 1 }
}

function buildColor(hex: string, opacity: number): string {
  if (opacity >= 1) return hex
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${Math.max(0, parseFloat(opacity.toFixed(2)))})`
}

// ── Shared building blocks for the floating style panels ─────────
// (box + image panels rendered in the element-controls stack)

const PANEL_W = 320
const dark = '#1e293b'
const dim = '#e2e8f0'
const numSt =
  'width:70px;height:30px;padding:0 6px;background:#0f172a;color:#e2e8f0;border:1px solid #334155;border-radius:4px;font-size:15px;outline:none;box-sizing:border-box;-moz-appearance:textfield;'
const selSt =
  'flex:1;min-width:0;height:30px;padding:0 5px;background:#0f172a;color:#e2e8f0;border:1px solid #334155;border-radius:4px;font-size:15px;outline:none;cursor:pointer;'
const rowSt = 'display:flex;align-items:center;gap:6px;'
const hdrSt =
  'display:flex;align-items:center;justify-content:space-between;margin-bottom:5px;'
const labSt =
  'font-size:13px;font-weight:600;text-transform:uppercase;letter-spacing:.07em;color:#cbd5e1;'

const BORDER_STYLES = ['solid', 'dashed', 'dotted', 'none']

const panelShellStyle = [
  `width:${PANEL_W}px; background:${dark}; border-radius:8px;`,
  'box-shadow:0 4px 24px rgba(0,0,0,.55); padding:10px 12px;',
  'display:flex; flex-direction:column; gap:10px; user-select:none;',
].join(' ')

function linkBtn(linked: boolean, onToggle: () => void) {
  return html.div(
    attr.style(
      `height:28px;padding:0 10px;border-radius:3px;cursor:pointer;flex-shrink:0;background:${linked ? '#3b82f6' : '#334155'};color:${linked ? '#fff' : dim};font-size:14px;display:inline-flex;align-items:center;`
    ),
    on.click((e: MouseEvent) => {
      e.stopPropagation()
      onToggle()
    }),
    linked ? 'All' : 'Each'
  )
}

function numEl(
  val: number,
  min: number,
  onChange: (v: number) => void,
  onInput?: (v: number) => void
) {
  return html.input(
    attr.type('number'),
    attr.style(numSt),
    Attr('value', String(val)),
    Attr('min', String(min)),
    // Live preview per keystroke / spinner tick (no commit, no re-render)
    on.input((e: Event) => {
      if (!onInput) return
      const v = parseFloat((e.target as HTMLInputElement).value)
      if (!isNaN(v)) onInput(Math.max(min, v))
    }),
    on.change((e: Event) => {
      const v = parseFloat((e.target as HTMLInputElement).value)
      if (!isNaN(v)) onChange(v)
    })
  )
}

function styleSelect(val: string, onChange: (v: string) => void) {
  return html.select(
    attr.style(selSt),
    on.change((e: Event) => onChange((e.target as HTMLSelectElement).value)),
    ...BORDER_STYLES.map(s =>
      s === val
        ? html.option(attr.value(s), Attr('selected', ''), s)
        : html.option(attr.value(s), s)
    )
  )
}

interface RadiusFields {
  radiusLinked?: boolean
  radiusTL?: number
  radiusTR?: number
  radiusBR?: number
  radiusBL?: number
}

/**
 * Live corner-rounding preview: writes border-radius straight onto the
 * component's display element while the user types or clicks the spinner.
 * The next committed re-render replaces the inline value with the model's.
 */
function radiusPreviewFor(compId: string) {
  return (tl: number, tr: number, br: number, bl: number) => {
    const el = document.querySelector<HTMLElement>(
      `[data-component-id="${compId}"]`
    )
    if (el) el.style.borderRadius = `${tl}px ${tr}px ${br}px ${bl}px`
  }
}

/**
 * "Corner Rounding" panel section, shared by the box and image panels:
 * a single linked value, or per-corner inputs behind the All/Each toggle.
 * Fields may be absent (older saved designs) — treated as 0 / linked.
 */
function CornerRoundingSection(
  comp: RadiusFields,
  upd: (changes: Partial<RadiusFields>) => void,
  preview: (tl: number, tr: number, br: number, bl: number) => void
) {
  const linked = comp.radiusLinked ?? true
  const tl = comp.radiusTL ?? 0
  const tr = comp.radiusTR ?? 0
  const br = comp.radiusBR ?? 0
  const bl = comp.radiusBL ?? 0

  function cornerInput(
    label: string,
    val: number,
    onChange: (v: number) => void,
    onInput: (v: number) => void
  ) {
    return html.div(
      attr.style(
        'display:flex;flex-direction:column;align-items:center;gap:2px;flex:1;'
      ),
      html.span(attr.style(`font-size:13px;color:${dim};`), label),
      numEl(val, 0, onChange, onInput)
    )
  }

  const body = linked
    ? html.div(
        attr.style(rowSt),
        numEl(
          tl,
          0,
          v => upd({ radiusTL: v, radiusTR: v, radiusBR: v, radiusBL: v }),
          v => preview(v, v, v, v)
        ),
        html.span(
          attr.style(`font-size:14px;color:${dim};`),
          'px — all corners'
        )
      )
    : html.div(
        attr.style('display:flex;flex-direction:column;gap:4px;'),
        html.div(
          attr.style('display:flex;gap:6px;'),
          cornerInput(
            '↖ TL',
            tl,
            v => upd({ radiusTL: v }),
            v => preview(v, tr, br, bl)
          ),
          cornerInput(
            '↗ TR',
            tr,
            v => upd({ radiusTR: v }),
            v => preview(tl, v, br, bl)
          )
        ),
        html.div(
          attr.style('display:flex;gap:6px;'),
          cornerInput(
            '↙ BL',
            bl,
            v => upd({ radiusBL: v }),
            v => preview(tl, tr, br, v)
          ),
          cornerInput(
            '↘ BR',
            br,
            v => upd({ radiusBR: v }),
            v => preview(tl, tr, v, bl)
          )
        )
      )

  return html.div(
    html.div(
      attr.style(hdrSt),
      html.span(attr.style(labSt), 'Corner Rounding'),
      linkBtn(linked, () => {
        if (linked) {
          upd({ radiusLinked: false })
        } else {
          upd({
            radiusLinked: true,
            radiusTR: tl,
            radiusBR: tl,
            radiusBL: tl,
          })
        }
      })
    ),
    body
  )
}

/**
 * Floating dark style panel below a selected box, ported from the sandbox:
 * background color/opacity, per-side or linked borders, and per-corner or
 * linked corner rounding. Color/width inputs live-preview by writing
 * straight to the component's DOM element (no signal re-render, which
 * would close the native color picker) and only commit on change.
 */
function ContainerToolbar(comp: ContainerBlock, actions: DesignActions) {
  function upd(changes: Partial<ContainerBlock>) {
    actions.updateComponent({ ...comp, ...changes }, true)
  }

  // Find the selected container's display element for live preview
  function findDisplayEl(): HTMLElement | null {
    return document.querySelector<HTMLElement>(
      `[data-component-id="${comp.id}"]`
    )
  }

  function colorEl(
    val: string,
    onChange: (v: string) => void,
    cssProps?: string[]
  ) {
    const { hex, opacity } = parseColor(val)
    const isOff = opacity <= 0

    // Live-preview: apply color directly to the container's DOM element
    // without triggering a signal re-render (which would close the native picker).
    function preview(color: string) {
      if (!cssProps) return
      const el = findDisplayEl()
      if (el) cssProps.forEach(p => el.style.setProperty(p, color))
    }

    return html.div(
      attr.style('display:flex;align-items:center;gap:6px;'),
      // Color swatch (left)
      html.input(
        attr.type('color'),
        attr.style(
          `width:46px;height:36px;padding:1px 2px;background:#0f172a;border:1px solid #475569;border-radius:4px;cursor:pointer;flex-shrink:0;${isOff ? 'opacity:0.3;' : ''}`
        ),
        Attr('value', hex),
        on.input((e: Event) => {
          const h = (e.target as HTMLInputElement).value
          preview(buildColor(h, isOff ? 1 : opacity))
          // Keep the visible hex value in step while dragging inside the
          // native picker (commit only fires on close).
          const hexField = (
            e.target as HTMLElement
          ).parentElement?.querySelector<HTMLInputElement>(
            'input[data-hex-field]'
          )
          if (hexField) hexField.value = h.toUpperCase()
        }),
        on.change((e: Event) => {
          onChange(
            buildColor(
              (e.target as HTMLInputElement).value,
              isOff ? 1 : opacity
            )
          )
        })
      ),
      // Editable hex value — OUR visible color format. (The swatch's popup
      // is the browser's built-in picker; its RGB-first format can't be
      // configured from the page.)
      html.input(
        attr.type('text'),
        Attr('data-hex-field', ''),
        Attr('maxlength', '7'),
        Attr('spellcheck', 'false'),
        Attr('title', 'Hex color'),
        Attr('value', hex.toUpperCase()),
        attr.style(
          `width:68px;height:34px;padding:0 5px;flex-shrink:0;text-align:center;background:#0f172a;color:#e2e8f0;border:1px solid #475569;border-radius:4px;font-size:12px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;outline:none;${isOff ? 'opacity:0.3;' : ''}`
        ),
        on.keydown((e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault()
            ;(e.target as HTMLInputElement).blur()
          }
        }),
        on.change((e: Event) => {
          const input = e.target as HTMLInputElement
          const norm = normalizeHexColor(input.value)
          // Snap the field to canonical form ("fff" → "#FFFFFF") even when
          // the color is unchanged and no re-render will do it.
          input.value = (norm ?? hex).toUpperCase()
          if (norm) onChange(buildColor(norm, isOff ? 1 : opacity))
        })
      ),
      // Opacity slider + percentage (middle, fills space)
      html.input(
        attr.type('range'),
        Attr('title', 'Opacity'),
        attr.style(
          'flex:1;height:6px;accent-color:#3b82f6;cursor:pointer;min-width:0;'
        ),
        Attr('min', '0'),
        Attr('max', '100'),
        Attr('value', String(Math.round(opacity * 100))),
        on.change((e: Event) => {
          onChange(
            buildColor(
              hex,
              parseInt((e.target as HTMLInputElement).value) / 100
            )
          )
        })
      ),
      html.input(
        attr.type('text'),
        Attr('maxlength', '3'),
        Attr('title', 'Opacity'),
        Attr('value', String(Math.round(opacity * 100))),
        attr.style(
          `width:40px;height:26px;padding:0 4px;text-align:right;flex-shrink:0;background:#0f172a;color:${dim};border:1px solid #334155;border-radius:4px;font-size:13px;outline:none;`
        ),
        on.keydown((e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault()
            ;(e.target as HTMLInputElement).blur()
          }
        }),
        on.change((e: Event) => {
          const v = parseInt((e.target as HTMLInputElement).value, 10)
          if (isNaN(v)) return
          onChange(buildColor(hex, Math.max(0, Math.min(100, v)) / 100))
        })
      ),
      html.span(attr.style(`font-size:13px;color:${dim};flex-shrink:0;`), '%'),
      // No-fill toggle (right)
      html.div(
        attr.style(
          `width:34px;height:34px;border-radius:4px;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0;border:1px solid ${isOff ? '#ef4444' : '#475569'};background:${isOff ? '#ef4444' : '#334155'};color:${isOff ? '#fff' : dim};`
        ),
        on.click((e: MouseEvent) => {
          e.stopPropagation()
          onChange(buildColor(hex, isOff ? 1 : 0))
        }),
        '⊘'
      )
    )
  }

  function borderRow(
    label: string,
    side: BorderSide,
    onChange: (s: BorderSide) => void,
    cssSides: string[]
  ) {
    return html.div(
      attr.style(
        'display:flex;flex-direction:column;gap:4px;padding:6px 0;border-bottom:1px solid #334155;'
      ),
      // Top line: label + width + style
      html.div(
        attr.style(rowSt),
        html.span(
          attr.style(
            `font-size:14px;color:${dim};width:20px;text-align:center;flex-shrink:0;`
          ),
          label
        ),
        html.input(
          attr.type('number'),
          attr.style(numSt),
          Attr('value', String(side.width)),
          Attr('min', '0'),
          on.input((e: Event) => {
            const v = parseFloat((e.target as HTMLInputElement).value)
            if (isNaN(v)) return
            const el = findDisplayEl()
            if (el)
              cssSides.forEach(s =>
                el.style.setProperty(`border-${s}-width`, `${v}px`)
              )
          }),
          on.change((e: Event) => {
            const v = parseFloat((e.target as HTMLInputElement).value)
            if (!isNaN(v)) onChange({ ...side, width: v })
          })
        ),
        styleSelect(side.style, s =>
          onChange({ ...side, style: s as BorderSide['style'] })
        )
      ),
      // Color control (indented to align with width input)
      html.div(
        attr.style('margin-left:26px;'),
        colorEl(
          side.color,
          c => onChange({ ...side, color: c }),
          cssSides.map(s => `border-${s}-color`)
        )
      )
    )
  }

  // ── Background ────────────────────────────────────────────────
  const bgSection = html.div(
    html.div(attr.style(hdrSt), html.span(attr.style(labSt), 'Background')),
    colorEl(comp.backgroundColor, v => upd({ backgroundColor: v }), [
      'background-color',
    ])
  )

  // ── Border ────────────────────────────────────────────────────
  const borderBody = comp.borderLinked
    ? borderRow(
        '—',
        comp.borderTop,
        s =>
          upd({
            borderTop: s,
            borderRight: { ...s },
            borderBottom: { ...s },
            borderLeft: { ...s },
          }),
        ['top', 'right', 'bottom', 'left']
      )
    : html.div(
        attr.style('display:flex;flex-direction:column;gap:3px;'),
        borderRow('↑', comp.borderTop, s => upd({ borderTop: s }), ['top']),
        borderRow('→', comp.borderRight, s => upd({ borderRight: s }), [
          'right',
        ]),
        borderRow('↓', comp.borderBottom, s => upd({ borderBottom: s }), [
          'bottom',
        ]),
        borderRow('←', comp.borderLeft, s => upd({ borderLeft: s }), ['left'])
      )

  const borderSection = html.div(
    html.div(
      attr.style(hdrSt),
      html.span(attr.style(labSt), 'Border'),
      linkBtn(comp.borderLinked, () => {
        if (comp.borderLinked) {
          upd({ borderLinked: false })
        } else {
          // syncing all sides to top when collapsing back to "All"
          const t = comp.borderTop
          upd({
            borderLinked: true,
            borderRight: { ...t },
            borderBottom: { ...t },
            borderLeft: { ...t },
          })
        }
      })
    ),
    borderBody
  )

  return html.div(
    attr.class('container-toolbar'),
    attr.style(panelShellStyle),
    on.click((e: MouseEvent) => e.stopPropagation()),
    on.mousedown((e: MouseEvent) => e.stopPropagation()),
    bgSection,
    borderSection,
    CornerRoundingSection(comp, upd, radiusPreviewFor(comp.id))
  )
}

/**
 * Floating style panel below a selected image: corner rounding, which acts
 * as a mask over the image (a large enough radius makes a square image
 * fully round).
 */
function ImageToolbar(comp: ImageBlock, actions: DesignActions) {
  function upd(changes: Partial<ImageBlock>) {
    actions.updateComponent({ ...comp, ...changes }, true)
  }

  return html.div(
    attr.class('image-toolbar'),
    attr.style(panelShellStyle),
    on.click((e: MouseEvent) => e.stopPropagation()),
    on.mousedown((e: MouseEvent) => e.stopPropagation()),
    CornerRoundingSection(comp, upd, radiusPreviewFor(comp.id))
  )
}

function renderComponent(
  compSignal: CompSignal,
  state: DesignEditorState,
  actions: DesignActions,
  agencyId: string,
  drag: DragController
) {
  const id = compSignal.value.id
  const type = compSignal.value.type
  if (type === 'text' || type === 'watermark') {
    return renderTextBlock(compSignal, id, state)
  }
  if (type === 'image')
    return renderImageBlock(compSignal, id, state, actions, agencyId, drag)
  if (type === 'resume') return renderResumeBlock(compSignal, id, state)
  return renderContainerBlock(compSignal, id, state, drag)
}

/**
 * Z-order controls: labeled buttons rendered as the bottom row of the
 * element-controls stack, directly beneath the element's other floating
 * controls (see PageCanvas).
 */
function ZOrderBar(
  comp: HeaderComponent,
  state: DesignEditorState,
  actions: DesignActions
) {
  const zBtnStyle = (enabled: boolean) =>
    [
      `height:32px; border:none; border-radius:2px; cursor:${enabled ? 'pointer' : 'default'};`,
      `background:${enabled ? '#334155' : '#1e293b'}; color:${enabled ? '#e2e8f0' : '#475569'};`,
      'display:flex; align-items:center; justify-content:center; gap:7px; padding:0 11px;',
      'font-size:14px; line-height:1; white-space:nowrap;',
    ].join(' ')

  const comps = state.components.value
  const compIdx = comps.findIndex(c => c.id === comp.id)
  // The resume block is always at index 0; other components treat index 1 as the floor
  const hasResume = comps[0]?.type === 'resume'
  const minIdx = hasResume ? 1 : 0
  const isFirst = compIdx <= minIdx
  const isLast = compIdx >= comps.length - 1

  // Layer icons: two overlapping rectangles — filled = active element, outline = other
  function layerIcon(variant: 'back' | 'backward' | 'forward' | 'front') {
    const sz = 11 // square size
    const off = 6 // offset between the two squares
    const W = sz + off
    const H = sz + off

    // For bring-forward/front: filled square is on top (in front)
    // For send-backward/back: filled square is behind (outline on top)
    const isFront = variant === 'front' || variant === 'forward'

    // Back square (bottom-left)
    const backSq = html.div(
      attr.style(
        [
          `position:absolute; left:0; top:${off}px;`,
          `width:${sz}px; height:${sz}px; border-radius:1px;`,
          isFront
            ? 'border:2px solid currentColor; opacity:0.4;'
            : 'background:currentColor;',
        ].join(' ')
      )
    )

    // Front square (top-right)
    const frontSq = html.div(
      attr.style(
        [
          `position:absolute; left:${off}px; top:0;`,
          `width:${sz}px; height:${sz}px; border-radius:1px;`,
          isFront
            ? 'background:currentColor;'
            : 'border:2px solid currentColor; opacity:0.4;',
        ].join(' ')
      )
    )

    // For "to back" / "to front" show a small arrow hint
    const hasArrow = variant === 'back' || variant === 'front'

    return html.div(
      attr.style(
        `position:relative; width:${W}px; height:${H}px; pointer-events:none;`
      ),
      backSq,
      frontSq,
      hasArrow
        ? html.div(
            attr.style(
              [
                'position:absolute;',
                variant === 'back'
                  ? `left:${sz + 1}px; top:${off + sz - 3}px;`
                  : '',
                variant === 'front' ? 'left:0px; top:1px;' : '',
                'width:0; height:0; border-left:4px solid transparent; border-right:4px solid transparent;',
                variant === 'back' ? 'border-top:5px solid currentColor;' : '',
                variant === 'front'
                  ? 'border-bottom:5px solid currentColor;'
                  : '',
              ].join(' ')
            )
          )
        : html.span()
    )
  }

  return html.div(
    attr.class('z-order-bar'),
    attr.style(
      [
        'display:flex; gap:2px;',
        // Bottom radius matches the text stack's shell so the panel's
        // bottom corners stay rounded; standalone (container/image) the
        // bar keeps its own background as before.
        'background:#1e293b; border-radius:0 0 6px 6px; padding:2px;',
        'border-top: 1px solid #334155;',
        'pointer-events:auto;',
      ].join(' ')
    ),
    on.mousedown((e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }),
    on.click((e: MouseEvent) => e.stopPropagation()),

    // Send to back (index 0)
    html.button(
      attr.style(zBtnStyle(!isFirst)),
      Attr('title', 'Move behind all other elements'),
      on.click(() => {
        if (!isFirst) actions.moveZ(compIdx, 0)
      }),
      layerIcon('back'),
      html.span(attr.style('pointer-events:none;'), 'Send to back')
    ),
    // Send backward (index - 1)
    html.button(
      attr.style(zBtnStyle(!isFirst)),
      Attr('title', 'Move back one layer'),
      on.click(() => {
        if (!isFirst) actions.moveZ(compIdx, compIdx - 1)
      }),
      layerIcon('backward'),
      html.span(attr.style('pointer-events:none;'), 'Backward')
    ),
    // Send forward (index + 1)
    html.button(
      attr.style(zBtnStyle(!isLast)),
      Attr('title', 'Move forward one layer'),
      on.click(() => {
        if (!isLast) actions.moveZ(compIdx, compIdx + 1)
      }),
      layerIcon('forward'),
      html.span(attr.style('pointer-events:none;'), 'Forward')
    ),
    // Send to front (last index)
    html.button(
      attr.style(zBtnStyle(!isLast)),
      Attr('title', 'Move in front of all other elements'),
      on.click(() => {
        if (!isLast) actions.moveZ(compIdx, comps.length - 1)
      }),
      layerIcon('front'),
      html.span(attr.style('pointer-events:none;'), 'Bring to front')
    )
  )
}

/**
 * Selection chrome, ported from the sandbox: square resize handles inside a
 * wrapper that rotates with the component, draggable edge strips for
 * moving, a red trash tab, and a rotate handle above.
 */
function ResizeHandles(
  state: DesignEditorState,
  actions: DesignActions,
  drag: ReturnType<typeof createDragController>
) {
  return MapSignal(state.selectedComponent, comp => {
    if (!comp) return html.div()
    const { x, y, width, height } = comp
    const rotation = comp.rotation || 0
    const S = 7
    const half = S / 2

    // Positions relative to component origin (for use inside rotated wrapper)
    const handles = [
      { id: 'nw', cx: 0, cy: 0, cursor: 'nw-resize' },
      { id: 'n', cx: width / 2, cy: 0, cursor: 'n-resize' },
      { id: 'ne', cx: width, cy: 0, cursor: 'ne-resize' },
      { id: 'e', cx: width, cy: height / 2, cursor: 'e-resize' },
      { id: 'se', cx: width, cy: height, cursor: 'se-resize' },
      { id: 's', cx: width / 2, cy: height, cursor: 's-resize' },
      { id: 'sw', cx: 0, cy: height, cursor: 'sw-resize' },
      { id: 'w', cx: 0, cy: height / 2, cursor: 'w-resize' },
    ]

    // Draggable edges: the whole border moves the component, matching user
    // expectation. Rendered before the resize squares so those stay on top.
    const EDGE = 10
    const edgeStrips = [
      { l: 0, t: -EDGE / 2, w: width, h: EDGE },
      { l: 0, t: height - EDGE / 2, w: width, h: EDGE },
      { l: -EDGE / 2, t: 0, w: EDGE, h: height },
      { l: width - EDGE / 2, t: 0, w: EDGE, h: height },
    ].map(s =>
      html.div(
        attr.style(
          [
            'position:absolute;',
            `left:${s.l}px; top:${s.t}px; width:${s.w}px; height:${s.h}px;`,
            'cursor:move; pointer-events:auto;',
          ].join(' ')
        ),
        on.mousedown((e: MouseEvent) => {
          state.selectedId.set(comp.id)
          drag.startDrag(e, comp.id, 'move')
        }),
        on.click((e: MouseEvent) => e.stopPropagation())
      )
    )

    // Delete handle: red trash tab to the right of the bottom-right corner
    const trashW = 27
    const trashH = 33
    const trashHandle = html.div(
      attr.style(
        [
          'position: absolute;',
          `left: ${width}px; top: ${height - trashH - 14}px;`,
          `width: ${trashW}px; height: ${trashH}px;`,
          'background: #ef4444; border-radius: 0 4px 4px 0;',
          'cursor: pointer; z-index: 20;',
          'display: flex; align-items: center; justify-content: center;',
          'user-select: none; pointer-events: auto;',
          `transform: rotate(${-rotation}deg);`,
        ].join(' ')
      ),
      Attr('title', 'Delete element'),
      on.mousedown((e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
      }),
      on.click((e: MouseEvent) => {
        e.stopPropagation()
        actions.deleteComponent(comp.id)
      }),
      // Trash can icon: lid with handle, tapered body, two slats
      html.div(
        attr.style('display: flex; pointer-events: none; line-height: 0;'),
        attr.innerHTML(
          '<svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M1.5 4.5h13" stroke="white" stroke-width="1.6" stroke-linecap="round"/>' +
            '<path d="M5.75 4.2V3a1.5 1.5 0 0 1 1.5-1.5h1.5a1.5 1.5 0 0 1 1.5 1.5v1.2" stroke="white" stroke-width="1.6"/>' +
            '<path d="M3.4 4.5l.9 10.7a1.6 1.6 0 0 0 1.6 1.47h4.2a1.6 1.6 0 0 0 1.6-1.47l.9-10.7" stroke="white" stroke-width="1.6" stroke-linejoin="round"/>' +
            '<path d="M6.4 7.8v5.6M9.6 7.8v5.6" stroke="white" stroke-width="1.4" stroke-linecap="round"/>' +
            '</svg>'
        )
      )
    )

    const isResume = comp.type === 'resume'

    // Rotate handle + line (relative to component origin, inside rotated wrapper)
    const rotateLineH = 28
    const rotateLine = html.div(
      attr.style(
        [
          'position:absolute;',
          `left:${width / 2}px; top:${-rotateLineH}px;`,
          `width:1px; height:${rotateLineH}px;`,
          'background:#3b82f6; pointer-events:none;',
        ].join(' ')
      )
    )
    const rotateHandle = html.div(
      attr.style(
        [
          'position:absolute;',
          `left:${width / 2 - 6}px; top:${-rotateLineH - 12}px;`,
          'width:12px; height:12px; border-radius:50%;',
          'background:white; border:2px solid #3b82f6;',
          'cursor:grab;',
          'display:flex; align-items:center; justify-content:center;',
          'pointer-events:auto;',
        ].join(' ')
      ),
      on.mousedown((e: MouseEvent) => {
        state.selectedId.set(comp.id)
        drag.startRotate(e, comp.id)
      }),
      on.click((e: MouseEvent) => e.stopPropagation()),
      html.div(
        attr.style(
          'width:8px; height:8px; border:1.5px solid #3b82f6; border-radius:50%; border-bottom-color:transparent; border-left-color:transparent; pointer-events:none;'
        )
      )
    )

    // Rotated wrapper: resize handles + rotate handle rotate with the component
    return html.div(
      attr.class('handles-wrapper'),
      attr.style(
        [
          `position:absolute; left:${x}px; top:${y}px;`,
          `width:${width}px; height:${height}px;`,
          `transform:rotate(${rotation}deg); transform-origin:center;`,
          'pointer-events:none; z-index:15; overflow:visible;',
        ].join(' ')
      ),
      ...edgeStrips,
      ...(isResume ? [] : [trashHandle]),
      ...(isResume ? [] : [rotateLine, rotateHandle]),
      ...handles.map(h =>
        html.div(
          attr.style(
            [
              'position: absolute;',
              `left: ${h.cx - half}px; top: ${h.cy - half}px;`,
              `width: ${S}px; height: ${S}px;`,
              'background: white; border: 1.5px solid #3b82f6;',
              `border-radius: 1px; cursor: ${h.cursor};`,
              'pointer-events: auto;',
            ].join(' ')
          ),
          on.mousedown((e: MouseEvent) => {
            state.selectedId.set(comp.id)
            drag.startDrag(e, comp.id, 'resize', h.id)
          }),
          on.click((e: MouseEvent) => e.stopPropagation())
        )
      )
    )
  })
}

export interface PageCanvasOptions {
  state: DesignEditorState
  actions: DesignActions
  agencyId: string
}

const PAGE_SELECTOR = '.design-canvas-page'

export function PageCanvas({ state, actions, agencyId }: PageCanvasOptions) {
  const drag = createDragController(
    state,
    actions,
    `.${PAGE_SELECTOR.slice(1)}`
  )

  const canvasStyle = computed(() => {
    const { h } = PAGE_SIZES[state.pageSize.value]
    return `position: relative; width: 100%; max-width: ${PAGE_SIZES[state.pageSize.value].w}px; height: ${h}px; background: #ffffff; overflow: visible; box-shadow: 0 1px 6px rgba(0,0,0,0.15); margin: 0 auto;`
  }, [state.pageSize])

  // Selecting on mousedown re-renders chrome (edge strips, resize squares)
  // under the pointer, so mouseup can land on a different element than
  // mousedown — the browser then retargets the click to their common
  // ancestor, this canvas div. Deselect only when the press also STARTED
  // on empty background, or selecting a component by its edge would
  // deselect it again on mouseup.
  let pressStartedOnBackground = false
  const isCanvasBackground = (target: EventTarget | null) => {
    const t = target as HTMLElement | null
    return Boolean(
      t &&
      !t.closest('[data-component-id]') &&
      !t.closest('.element-controls') &&
      !t.closest('.editing-overlay') &&
      !t.closest('.handles-wrapper')
    )
  }

  return html.div(
    attr.class(PAGE_SELECTOR.slice(1)),
    attr.style(canvasStyle),
    on.mousedown((e: MouseEvent) => {
      pressStartedOnBackground = isCanvasBackground(e.target)
    }),
    on.click((e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (t.closest('.element-controls') || t.closest('.editing-overlay'))
        return
      if (!pressStartedOnBackground) return
      state.selectedId.set(null)
    }),
    // designEpoch remount: wholesale component swaps (wizard hand-off,
    // undo/redo) confuse the keyed diff into reusing positional subtrees
    // across component types; a bump unmounts the whole list first.
    MapSignal(state.designEpoch, () =>
      KeyedForEach(
        state.components,
        c => c.id,
        (compSignal: CompSignal) =>
          renderComponent(compSignal, state, actions, agencyId, drag)
      )
    ),
    // Element controls stack: the element-specific controls (formatting
    // toolbar for text, style panel for containers) sit flush against the
    // component's bottom border, with the z-order bar directly beneath them.
    MapSignal(state.selectedComponent, comp => {
      if (!comp) return html.div()
      const isText = comp.type === 'text' || comp.type === 'watermark'
      const isContainer = comp.type === 'container'
      const isImage = comp.type === 'image'
      const showZOrder = comp.type !== 'resume' && comp.id !== WATERMARK_ID
      if (!isText && !isContainer && !isImage && !showZOrder) return html.div()

      const { w: pageW, h: pageH } = PAGE_SIZES[state.pageSize.value]

      return html.div(
        attr.class('element-controls'),
        // The taller box/image panels may need to move beside their
        // component; the flatter toolbar/z-order rows only ever flip
        // above. Element-bound so every rebuild positions its own node.
        WithElement((el: HTMLElement) => {
          if (isContainer || isImage) {
            placePanelClearOfComponent(el, comp, pageW, pageH)
          } else {
            keepToolbarOnPage(el, comp, pageW, pageH)
          }
        }),
        attr.style(
          [
            `position:absolute; left:${comp.x}px; top:${comp.y + comp.height}px;`,
            'display:flex; flex-direction:column; align-items:flex-start;',
            'z-index:20;',
            // Text controls render as multiple transparent rows; the stack
            // provides the shared dark shell so they read as one panel.
            ...(isText
              ? [
                  'background:#1e293b; border-radius:6px;',
                  'box-shadow:0 2px 12px rgba(0,0,0,0.4);',
                ]
              : []),
          ].join(' ')
        ),
        on.click((e: MouseEvent) => e.stopPropagation()),
        ...(isText ? [renderFormattingToolbar(comp, actions)] : []),
        ...(isContainer ? [ContainerToolbar(comp, actions)] : []),
        ...(isImage ? [ImageToolbar(comp, actions)] : []),
        ...(showZOrder ? [ZOrderBar(comp, state, actions)] : [])
      )
    }),
    MapSignal(state.selectedComponent, comp => {
      if (!comp || (comp.type !== 'text' && comp.type !== 'watermark'))
        return html.div()
      return renderEditingOverlay(comp, state, actions)
    }),
    ResizeHandles(state, actions, drag),

    // Grid overlay — on top of everything, pointer-events:none so it doesn't interfere
    html.div(
      attr.class('grid-overlay'),
      attr.style(
        computed(() => {
          if (!state.gridVisible.value) return 'display:none;'
          const sz = gridCellSize(state)
          const svg =
            `<svg xmlns='http://www.w3.org/2000/svg' width='${sz}' height='${sz}'>` +
            `<line x1='0' y1='0' x2='${sz}' y2='0' stroke='%23475569' stroke-width='0.5' stroke-dasharray='3,3' opacity='0.5'/>` +
            `<line x1='0' y1='0' x2='0' y2='${sz}' stroke='%23475569' stroke-width='0.5' stroke-dasharray='3,3' opacity='0.5'/>` +
            '</svg>'
          return `position:absolute;inset:0;pointer-events:none;z-index:100;background-image:url("data:image/svg+xml,${svg}");background-size:${sz}px ${sz}px;background-repeat:repeat;`
        }, [state.gridVisible, state.gridSize, state.pageSize])
      )
    )
  )
}
